import React, { useState } from "react";

import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import {
  MdExpandMore,
  MdChevronLeft,
} from "react-icons/md";

import * as MdIcons from "react-icons/md";
import * as FaIcons from "react-icons/fa";
import * as IoIcons from "react-icons/io5";
import * as BiIcons from "react-icons/bi";
import * as HiIcons from "react-icons/hi2";

import {
  Link,
  useLocation,
} from "react-router-dom";

import helper from "../../helper";

const menuColors = {
    text: "#000",
  hoverBackground: "rgb(246, 246, 247)",
  selectedBackground: "#c6c7c9",
  selectedText: "#000",
  separator: "#DDDCD4",
};

const menuTypography = {
  level0: "12.8px",
  level1: "12.8px",
  level2: "12.4px",

  level0FontFamily: "Vazir-Bold",
  level1FontFamily: "Vazir-Medium",
  level2FontFamily: "Vazir-Medium",
};

const menuSettings = {
  showSeparator: true,

  // ارتفاع آیتم‌های منو
  itemHeight: {
    level0: 40,
    level1: 40,
    level2: 40,
  },

  // فاصله پایین هر آیتم
  itemMarginBottom: {
    level0: 0.15,
    level1: 0.15,
    level2: 0.15,
  },

  // فاصله عمودی جداکننده
  separatorMarginY: 0.5,
};

const getMenuIcon = (iconName) => {
  if (!iconName) return null;

  return (
    MdIcons[iconName] ||
    FaIcons[iconName] ||
    IoIcons[iconName] ||
    BiIcons[iconName] ||
    HiIcons[iconName] ||
    null
  );
};

const hasSelectedChild = (item, pathname) => {
  if (!item.children || item.children.length === 0) {
    return false;
  }

  return item.children.some((child) => {
    const childPath =
      helper.GetPath(child.menuName, child.params) || "";

    const childIsSelected =
      childPath &&
      pathname
        .substring(11)
        .toLowerCase()
        .startsWith(childPath.toLowerCase());

    return (
      childIsSelected ||
      hasSelectedChild(child, pathname)
    );
  });
};

const MenuItem = ({
  item,
  level,
  openItems,
  setOpenItems,
}) => {
  const location = useLocation();

  const hasChildren =
    item.children && item.children.length > 0;

  const isOpen =
    openItems[level] === undefined
      ? hasSelectedChild(item, location.pathname)
      : openItems[level] === item.menuId;

  const itemPath =
    helper.GetPath(item.menuName, item.params) || "";

  const isSelected =
    !hasChildren && itemPath
      ? location.pathname
          .substring(11)
          .toLowerCase()
          .startsWith(itemPath.toLowerCase())
      : false;

  const Icon = getMenuIcon(item.icon);

  const handleClick = () => {
    if (hasChildren) {
      setOpenItems((prev) => {
        const next = { ...prev };

        if (
          next[level] === item.menuId ||
          (next[level] === undefined &&
            hasSelectedChild(item, location.pathname))
        ) {
          next[level] = false;
        } else {
          next[level] = item.menuId;
        }

        Object.keys(next).forEach((key) => {
          if (Number(key) > level) {
            delete next[key];
          }
        });

        return next;
      });
    }
  };

  const getFontFamily = () => {
    if (level === 0) {
      return menuTypography.level0FontFamily;
    }

    if (level === 1) {
      return menuTypography.level1FontFamily;
    }

    return menuTypography.level2FontFamily;
  };

  const getFontSize = () => {
    if (level === 0) {
      return menuTypography.level0;
    }

    if (level === 1) {
      return menuTypography.level1;
    }

    return menuTypography.level2;
  };

  return (
    <>
      <ListItemButton
        component={!hasChildren ? Link : undefined}
        to={
          !hasChildren
            ? helper.GetPath(item.menuName, item.params)
            : undefined
        }
        onClick={handleClick}
        disableGutters
        sx={{
          minHeight:
            menuSettings.itemHeight[`level${level}`] ??
            menuSettings.itemHeight.level2,

          mx: 1,

          mb:
            menuSettings.itemMarginBottom[`level${level}`] ??
            menuSettings.itemMarginBottom.level2,

          px: 0,
          borderRadius: "8px",
          boxSizing: "border-box",
          minWidth: 0,
          backgroundColor: "transparent",
          color: menuColors.text,
          transition: "all 0.2s ease",

          "&:hover": {
            backgroundColor: menuColors.hoverBackground,
            color: menuColors.text,
          },

          ...(isOpen && {
            backgroundColor: "transparent",
            color: menuColors.text,
          }),

          ...(isSelected && {
            backgroundColor: menuColors.selectedBackground,
            color: menuColors.selectedText,

            "&:hover": {
              backgroundColor: menuColors.selectedBackground,
            },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            width: 32,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "inherit",
            ml: 1,
            order: 1,
          }}
        >
          {level === 0 && Icon && <Icon size={20} />}
        </ListItemIcon>

        {hasChildren && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 0.5,
              flexShrink: 0,
              color: "inherit",
            }}
          >
            {isOpen ? (
              <MdExpandMore size={20} />
            ) : (
              <MdChevronLeft size={20} />
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            pl: `${level * 16}px`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ListItemText
            primary={item.title}
            sx={{
              flex: 1,
              minWidth: 0,
              margin: 0,

              "& .MuiTypography-root": {
                textAlign: "end",
                fontFamily: getFontFamily(),
                fontSize: getFontSize(),
                fontWeight: 500,
                color: menuColors.text,
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordBreak: "normal",
                lineHeight: 1.6,
              },
            }}
          />
        </Box>
      </ListItemButton>

      {hasChildren && (
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
        >
          <List
            disablePadding
            sx={{
              width: "100%",
            }}
          >
            {item.children.map((child) => (
              <MenuItem
                key={child.menuId}
                item={child}
                level={level + 1}
                openItems={openItems}
                setOpenItems={setOpenItems}
              />
            ))}
          </List>
        </Collapse>
      )}

      {level === 0 && menuSettings.showSeparator && (
        <Box
          sx={{
            height: "1px",
            backgroundColor: menuColors.separator,
            mx: 2,
            my: menuSettings.separatorMarginY,
          }}
        />
      )}
    </>
  );
};

const HomeMenuItem = ({ setOpenItems }) => {
  const location = useLocation();

  const isSelected =
    location.pathname === "/dashboard";

  return (
    <ListItemButton
      component={Link}
      to="/dashboard"
      onClick={() => {
        setOpenItems({});
      }}
      sx={{
        minHeight: menuSettings.itemHeight.level0,
        mx: 1,
        mb: menuSettings.itemMarginBottom.level0,
        px: 0,
        borderRadius: "8px",
        boxSizing: "border-box",
        minWidth: 0,
        backgroundColor: "transparent",
        color: menuColors.text,
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: menuColors.hoverBackground,
          color: menuColors.text,
        },

        ...(isSelected && {
          backgroundColor: menuColors.selectedBackground,
          color: menuColors.selectedText,

          "&:hover": {
            backgroundColor: menuColors.selectedBackground,
          },
        }),
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          width: 32,
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "inherit",
          ml: 1,
          order: 1,
        }}
      >
        <MdIcons.MdDashboard size={20} />
      </ListItemIcon>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <ListItemText
          primary="صفحه اصلی"
          sx={{
            flex: 1,
            minWidth: 0,
            margin: 0,

            "& .MuiTypography-root": {
              textAlign: "end",
              fontFamily: menuTypography.level0FontFamily,
              fontSize: menuTypography.level0,
              fontWeight: 500,
              color: menuColors.text,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              wordBreak: "normal",
              lineHeight: 1.6,
            },
          }}
        />
      </Box>
    </ListItemButton>
  );
};

const VerticalMenu = ({ menuData = [] }) => {
  const [openItems, setOpenItems] = useState({});

  return (
    <Box
      sx={{
        width: "100%",
        direction: "rtl",
      }}
    >
      <List
        disablePadding
        sx={{
          width: "100%",
        }}
      >
        <HomeMenuItem
          setOpenItems={setOpenItems}
        />

        {menuData.map((item) => (
          <MenuItem
            key={item.menuId}
            item={item}
            level={0}
            openItems={openItems}
            setOpenItems={setOpenItems}
          />
        ))}
      </List>
    </Box>
  );
};

export default VerticalMenu;